import { useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import { playbookPage, renderMarkdown } from '../../lib/markdown';
import type { CsTool } from '../../lib/types';
import { LogoPicker, LogoTile, PortalLink } from './csUi';

/**
 * Tools — the launcher, ported from the CS Toolbox.
 *
 * One card per tool: its logo, the two portals worth a link (the tool's own
 * console and the partner/reseller portal), the commercials, and a Markdown
 * playbook that renders as a page in place or opens standalone in a new tab.
 *
 * The commercial fields are free text here as they were there. They arrive as
 * "included in BDE" and "per endpoint, min 25" — see migration 038.
 */

type Editing = { tool: CsTool | null } | null;

export function CsTools({ onChanged }: { onChanged: () => void }) {
  const tools = useResource<CsTool[]>('/cs/tools');
  const [editing, setEditing] = useState<Editing>(null);
  const [playbookFor, setPlaybookFor] = useState<CsTool | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    tools.reload();
    onChanged();
  };

  const remove = (tool: CsTool) => {
    api
      .del(`/cs/tools/${tool.CsToolId}`)
      .then(refresh)
      .catch((e: Error) => setError(e.message));
  };

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small">
          Every tool we run, what it costs, what we sell it for, and how to work it.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ tool: null })}>
          Add a tool
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {tools.error && <ErrorBanner message={tools.error} />}
      {tools.loading && <Loading label="Loading tools" />}

      {!tools.loading &&
        (tools.data?.length === 0 ? (
          <Empty title="No tools yet">
            <p className="muted small">
              Add the first one, or import the CS Toolbox's twenty-two with{' '}
              <span className="mono">npm run import:cs</span>.
            </p>
          </Empty>
        ) : (
          <div className="grid grid-3">
            {(tools.data ?? []).map((tool) => (
              <ToolCard
                key={tool.CsToolId}
                tool={tool}
                onEdit={() => setEditing({ tool })}
                onPlaybook={() => setPlaybookFor(tool)}
                onDelete={() => remove(tool)}
              />
            ))}
          </div>
        ))}

      {editing && (
        <ToolEditor
          tool={editing.tool}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      {playbookFor && (
        <PlaybookEditor
          tool={playbookFor}
          onClose={() => setPlaybookFor(null)}
          onSaved={() => {
            setPlaybookFor(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function ToolCard({
  tool,
  onEdit,
  onPlaybook,
  onDelete,
}: {
  tool: CsTool;
  onEdit: () => void;
  onPlaybook: () => void;
  onDelete: () => void;
}) {
  const commercials: { label: string; value: string | null }[] = [
    { label: 'Cost', value: tool.Cost },
    { label: 'Sell', value: tool.SellPrice },
    { label: 'Model', value: tool.PricingModel },
    { label: 'Agreement ends', value: tool.AgreementExpires },
  ];
  const shown = commercials.filter((c) => c.value);

  return (
    <section className="card cs-tool">
      <div className="card-head">
        <LogoTile path={tool.HasLogo ? `/cs/tools/${tool.CsToolId}/logo` : null} name={tool.Name} />
        <div className="grow">
          <h3>{tool.Name}</h3>
          <div className="row wrap">
            <PortalLink href={tool.ToolUrl}>Tool portal</PortalLink>
            <PortalLink href={tool.PartnerUrl}>Partner portal</PortalLink>
            {!tool.ToolUrl && !tool.PartnerUrl && <span className="xs subtle">No portal link</span>}
          </div>
        </div>
      </div>

      <div className="card-body">
        {shown.length > 0 && (
          <dl className="cs-commercials">
            {shown.map((c) => (
              <div key={c.label}>
                <dt>{c.label}</dt>
                <dd>{c.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="row wrap mt-3">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onPlaybook}>
            {tool.Playbook ? 'Playbook' : 'Write a playbook'}
          </button>
          {tool.Playbook && (
            <span className="badge badge-neutral" title="This tool has a playbook">
              documented
            </span>
          )}
        </div>
      </div>

      <div className="card-foot row-between">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
          Edit
        </button>
        <DeleteButton what={tool.Name} onConfirm={onDelete} />
      </div>
    </section>
  );
}

function ToolEditor({
  tool,
  onClose,
  onSaved,
}: {
  tool: CsTool | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tool?.Name ?? '');
  const [toolUrl, setToolUrl] = useState(tool?.ToolUrl ?? '');
  const [partnerUrl, setPartnerUrl] = useState(tool?.PartnerUrl ?? '');
  const [cost, setCost] = useState(tool?.Cost ?? '');
  const [sellPrice, setSellPrice] = useState(tool?.SellPrice ?? '');
  const [pricingModel, setPricingModel] = useState(tool?.PricingModel ?? '');
  const [agreementExpires, setAgreementExpires] = useState(tool?.AgreementExpires ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = () => {
    if (!name.trim()) {
      setError('A tool needs a name.');
      return;
    }
    setSaving(true);
    setError(null);
    const body = { name, toolUrl, partnerUrl, cost, sellPrice, pricingModel, agreementExpires };
    const request = tool
      ? api.put<CsTool>(`/cs/tools/${tool.CsToolId}`, body)
      : api.post<CsTool>('/cs/tools', body);
    request
      .then(onSaved)
      .catch((e: Error) => {
        setError(e.message);
        setSaving(false);
      });
  };

  /* Logo changes save immediately rather than on Save — they are their own
     endpoint, and holding an image in component state until the form is
     submitted means a failed upload looks like a saved one. */
  const uploadLogo = (file: { fileName: string; content: string }) => {
    if (!tool) {
      setError('Save the tool first, then add its logo.');
      return;
    }
    api
      .put(`/cs/tools/${tool.CsToolId}/logo`, file)
      .then(onSaved)
      .catch((e: Error) => setError(e.message));
  };

  const removeLogo = () => {
    if (!tool) return;
    api
      .del(`/cs/tools/${tool.CsToolId}/logo`)
      .then(onSaved)
      .catch((e: Error) => setError(e.message));
  };

  return (
    <Modal
      title={tool ? tool.Name : 'Add a tool'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Name">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>

      <div className="grid grid-2">
        <Field label="Tool portal" hint="Where you sign in to use it">
          <input className="input" value={toolUrl} onChange={(e) => setToolUrl(e.target.value)} placeholder="https://…" />
        </Field>
        <Field label="Partner portal" hint="Where you manage licensing and billing">
          <input
            className="input"
            value={partnerUrl}
            onChange={(e) => setPartnerUrl(e.target.value)}
            placeholder="https://…"
          />
        </Field>
      </div>

      <div className="grid grid-2">
        <Field label="Cost">
          <input className="input" value={cost} onChange={(e) => setCost(e.target.value)} />
        </Field>
        <Field label="Sell price">
          <input className="input" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} />
        </Field>
        <Field label="Pricing model" hint="Per endpoint, per user, flat…">
          <input className="input" value={pricingModel} onChange={(e) => setPricingModel(e.target.value)} />
        </Field>
        <Field label="Agreement expires">
          <input
            className="input"
            value={agreementExpires}
            onChange={(e) => setAgreementExpires(e.target.value)}
          />
        </Field>
      </div>

      <div className="field">
        <label>Logo</label>
        {tool ? (
          <div className="row wrap">
            <LogoTile path={tool.HasLogo ? `/cs/tools/${tool.CsToolId}/logo` : null} name={tool.Name} />
            <LogoPicker onPicked={uploadLogo} onError={setError} label={tool.HasLogo ? 'Replace' : 'Upload'} />
            {tool.HasLogo && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={removeLogo}>
                Remove
              </button>
            )}
          </div>
        ) : (
          <span className="xs subtle">Save the tool first, then add its logo.</span>
        )}
      </div>
    </Modal>
  );
}

/**
 * The playbook: read it, edit it, or open it as a standalone page.
 *
 * Standalone opens a blob rather than a route, which is how the original
 * behaved and is also the only option here — a new tab carries no bearer token,
 * so it could not fetch the content itself.
 */
function PlaybookEditor({
  tool,
  onClose,
  onSaved,
}: {
  tool: CsTool;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(tool.Playbook ?? '');
  const [mode, setMode] = useState<'read' | 'write'>(tool.Playbook ? 'read' : 'write');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = () => {
    setSaving(true);
    setError(null);
    api
      .put(`/cs/tools/${tool.CsToolId}/playbook`, { playbook: draft })
      .then(onSaved)
      .catch((e: Error) => {
        setError(e.message);
        setSaving(false);
      });
  };

  const openStandalone = () => {
    const blob = new Blob([playbookPage(tool.Name, draft)], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    /* Revoked on a delay rather than immediately: revoking before the new tab
       has finished loading gives it a blank page. */
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <Modal
      title={`${tool.Name} — playbook`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={openStandalone}>
            Open as a full page
          </button>
          <span className="grow" />
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="tabs mb-4">
        <button
          type="button"
          className={`tab${mode === 'read' ? ' active' : ''}`}
          onClick={() => setMode('read')}
        >
          Read
        </button>
        <button
          type="button"
          className={`tab${mode === 'write' ? ' active' : ''}`}
          onClick={() => setMode('write')}
        >
          Write
        </button>
      </div>

      {mode === 'read' ? (
        draft.trim() ? (
          <div className="playbook" dangerouslySetInnerHTML={{ __html: renderMarkdown(draft) }} />
        ) : (
          <p className="muted small">This playbook is empty. Switch to Write to start it.</p>
        )
      ) : (
        <>
          <textarea
            className="input mono"
            rows={20}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={'# Getting started\n\n1. Sign in at …\n\n**Note:** …'}
          />
          <p className="xs subtle mt-2">
            Markdown: # headings, - lists, **bold**, `code`, &gt; quotes, ``` fenced blocks, and
            [links](https://example.com).
          </p>
        </>
      )}
    </Modal>
  );
}
